/* v6.35.7 — Browser desktop notification on long-running-job completion.
 *
 * The audio chime fires regardless of tab focus, but it's easy to miss
 * if the operator's laptop is muted or in another room. The Notification
 * API surfaces a system-level banner outside the browser, so the operator
 * sees it even when the tab is hidden or the browser is minimized.
 *
 * Contract:
 *   - ensureNotificationPermission()  — call on user gesture (e.g. Run-Audit
 *     button click). Browsers REQUIRE a gesture to grant the permission.
 *     Returns boolean granted.
 *   - notifyOnCompletion({title, body, success})  — non-blocking; silently
 *     no-ops if permission isn't granted, the tab is focused, or the API
 *     isn't supported (Safari iOS).
 */

export async function ensureNotificationPermission() {
  try {
    if (typeof window === 'undefined') return false;
    if (!('Notification' in window)) return false;
    if (window.Notification.permission === 'granted') return true;
    if (window.Notification.permission === 'denied') return false;
    const result = await window.Notification.requestPermission();
    return result === 'granted';
  } catch {
    return false;
  }
}

export function notifyOnCompletion({ title, body, success = true }) {
  try {
    if (typeof window === 'undefined') return;
    if (!('Notification' in window)) return;
    if (window.Notification.permission !== 'granted') return;
    // Don't double-notify if the operator is already watching the tab.
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') return;
    const n = new window.Notification(title, {
      body,
      // v6.35.7 — tag dedupes notifications when multiple polls land in
      // the same tick. Browsers replace prior notifications with the
      // same tag instead of stacking.
      tag: `mosaic-job-${success ? 'success' : 'failure'}`,
      // Persistent for failure (user must dismiss); auto-dismiss success.
      requireInteraction: !success,
      silent: false,
    });
    // Bring the tab to focus on click.
    n.onclick = () => {
      try {
        window.focus();
        n.close();
      } catch {
        /* no-op */
      }
    };
  } catch {
    /* no-op — observability is best-effort */
  }
}
